<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'Home',
})
</script>
<template>
  <div class="home-container">
    <!-- 1. 顶部Banner区：系统标识 + 快速操作 -->


    <!-- 2. 核心功能入口区：3列布局，高频功能优先 -->
    <div class="home-function">
      <a-card class="function-card" @click="handleGoTo('/test')">
        <div class="card-icon dict-icon"></div>
        <h3 class="card-title">字典管理</h3>
        <p class="card-desc">新增/编辑/删除字典数据，维护通用、西医、中医等分类字典</p>
        <div class="card-count">{{ dictCount }} 个字典分类</div>
      </a-card>

      <a-card class="function-card" @click="handleGoTo('/system/module/index.vue')">
        <div class="card-icon query-icon"></div>
        <h3 class="card-title">数据查询</h3>
        <p class="card-desc">按指标树筛选数据，支持多周期对比，生成可视化趋势图表</p>
        <div class="card-count">{{ queryRecord }} 条今日查询记录</div>
      </a-card>

      <a-card class="function-card" @click="handleGoTo('/system/report/list')">
        <div class="card-icon export-icon"></div>
        <h3 class="card-title">报表管理</h3>
        <p class="card-desc">导出指标数据为Excel，支持自定义周期范围和字段筛选</p>
        <div class="card-count">{{ exportCount }} 次今日导出</div>
      </a-card>
    </div>

    <!-- 3. 数据概览区：2列布局，展示关键指标 -->
    <div class="home-overview">
      <a-card class="overview-card">
        <h3 class="overview-title">系统数据概览</h3>
        <div class="overview-grid">
          <div class="overview-item">
            <span class="item-label">总字典条目</span>
            <span class="item-value">{{ totalDictItem }} 条</span>
          </div>
          <div class="overview-item">
            <span class="item-label">今日新增条目</span>
            <span class="item-value add-value">{{ todayAddItem }} 条</span>
          </div>
          <div class="overview-item">
            <span class="item-label">待审核条目</span>
            <span class="item-value pending-value">{{ pendingItem }} 条</span>
          </div>
          <div class="overview-item">
            <span class="item-label">系统在线用户</span>
            <span class="item-value">{{ onlineUser }} 人</span>
          </div>
        </div>
      </a-card>

      <a-card class="overview-card">
        <h3 class="overview-title">最近操作记录</h3>
        <a-table
          :data-source="recentLogs"
          :columns="logColumns"
          row-key="id"
          :pagination="false"
          bordered
          class="log-table"
        />
      </a-card>
    </div>

    <!-- 4. 底部信息区：版权 + 帮助入口 -->
    <div class="home-footer">
      <div class="footer-left">© 2025 医疗数据字典管理系统 - 医院信息科出品</div>
      <div class="footer-right">
        <a-button type="text" @click="handleOpenHelp">使用帮助</a-button>
        <a-button type="text" @click="handleOpenFeedback">意见反馈</a-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup name="Home">
import { ref, onMounted } from "vue";
import { CSSProperties } from "vue";
import { SyncOutlined } from "@ant-design/icons-vue";
import { Button, Card, Table } from "ant-design-vue";
import router from "@/router";
import f, { sysInfo } from "@/utils/f.ts";
import DM from "@/utils/dm.vue";

// 1. 状态定义：模拟数据（实际项目中从接口获取）
const dictCount = ref(6); // 字典分类数（通用、西医、中医等）
const queryRecord = ref(12); // 今日查询记录
const exportCount = ref(8); // 今日导出次数
const totalDictItem = ref(1258); // 总字典条目
const todayAddItem = ref(23); // 今日新增条目
const pendingItem = ref(7); // 待审核条目
const onlineUser = ref(5); // 在线用户数

// 2. 最近操作日志数据
const recentLogs = ref([
  {
    id: 1,
    operator: sysInfo.UserName || sysInfo.UserID,
    action: "新增字典",
    target: "通用字典-性别分类",
    time: "2025-12-10 09:23:45",
    status: "成功",
  },
  {
    id: 2,
    operator: "张医生",
    action: "导出报表",
    target: "西医诊断指标（202511-202512）",
    time: "2025-12-10 08:15:22",
    status: "成功",
  },
  {
    id: 3,
    operator: sysInfo.UserName || sysInfo.UserID,
    action: "编辑字典",
    target: "手术字典-阑尾切除术",
    time: "2025-12-09 16:48:10",
    status: "成功",
  },
  {
    id: 4,
    operator: "李护士",
    action: "查询数据",
    target: "中医体质分类指标",
    time: "2025-12-09 14:30:05",
    status: "成功",
  },
]);

// 3. 日志表格列配置
const logColumns = ref([
  {
    title: "操作人",
    dataIndex: "operator",
    key: "operator",
    width: 120,
  },
  {
    title: "操作类型",
    dataIndex: "action",
    key: "action",
    width: 120,

  },
  {
    title: "操作对象",
    dataIndex: "target",
    key: "target",
    ellipsis: true,
    tooltip: true,
  },
  {
    title: "操作时间",
    dataIndex: "time",
    key: "time",
    width: 180,
  },
  {
    title: "状态",
    dataIndex: "status",
    key: "status",
    width: 80,

  },
]);

// 4. 生命周期：初始化时加载真实数据（替换模拟数据）
onMounted(async () => {
  await loadSystemData();
});

// 5. 核心方法：加载系统数据（实际项目中调用后端接口）
const loadSystemData = async () => {
  // try {
  //   // 示例：调用接口获取字典统计数据
  //   const dictStats = await dm.value.data("/api/comm/data/", {
  //     apicode: "SQLScript_GetDictStats",
  //     usercode: sysInfo.UserID,
  //   });

  //   // 示例：调用接口获取操作日志
  //   const logData = await dm.value.data("/api/comm/data/", {
  //     apicode: "SQLScript_GetRecentLogs",
  //     usercode: sysInfo.UserID,
  //     limit: 4,
  //   });

  //   // 替换模拟数据（实际项目中根据接口返回适配）
  //   if (dictStats?.status === 0 && dictStats.data?.list) {
  //     const stats = dictStats.data.list[0];
  //     dictCount.value = stats.dictCount || 6;
  //     totalDictItem.value = stats.totalDictItem || 1258;
  //     todayAddItem.value = stats.todayAddItem || 23;
  //     pendingItem.value = stats.pendingItem || 7;
  //     onlineUser.value = stats.onlineUser || 5;
  //   }

  //   if (logData?.status === 0 && logData.data?.list) {
  //     recentLogs.value = logData.data.list;
  //   }
  // } catch (error) {
  //   console.error("加载系统数据失败：", error);
  //   f.error("数据加载失败，请重试");
  // }
};

// 6. 事件方法：页面跳转、刷新、帮助等
const handleGoTo = (path: string) => {
  router.push(path);
};

const handleRefresh = async () => {
  f.info("正在刷新数据...");
  await loadSystemData();
  f.success("数据刷新成功！");
};

const handleOpenHelp = () => {
  // 打开帮助文档（可替换为实际帮助页面路径）
  router.push("/help");
  // 或打开弹窗展示帮助内容
  // helpModalVisible.value = true;
};

const handleOpenFeedback = () => {
  // 打开意见反馈弹窗（可自行实现）
  f.info("意见反馈功能开发中...");
};
</script>

<style scoped lang="less">
// 全局容器样式
.home-container {
  width: 100%;
  min-height: calc(100vh - 65px);
  padding: 16px 24px;
  background-color: #f5f7fa;
}

// 1. 顶部Banner样式
.home-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e8e8e8;

  .banner-left {
    .system-title {
      font-size: 24px;
      font-weight: 600;
      color: #1890ff;
      margin-bottom: 8px;
    }

    .system-desc {
      font-size: 14px;
      color: #666;
    }
  }

  .banner-right {
    display: flex;
    align-items: center;
    gap: 24px;

    .user-info {
      text-align: right;
      .user-role {
        font-size: 12px;
        color: #999;
        display: block;
        margin-bottom: 4px;
      }

      .user-name {
        font-size: 14px;
        color: #333;
        font-weight: 500;
      }
    }

    .refresh-btn {
      color: #1890ff;
      &:hover {
        background-color: #e6f7ff;
      }
    }
  }
}

// 2. 功能入口卡片样式
.home-function {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;

  .function-card {
    flex: 1;
    height: 240px;
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 8px !important;
    overflow: hidden;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
    }

    .card-icon {
      width: 48px;
      height: 48px;
      margin-bottom: 16px;
      border-radius: 8px;
      background-color: #e6f7ff;
      background-size: 24px;
      background-repeat: no-repeat;
      background-position: center;
    }

    .dict-icon {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%231890ff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='8' y1='6' x2='21' y2='6'/%3E%3Cline x1='8' y1='12' x2='21' y2='12'/%3E%3Cline x1='8' y1='18' x2='21' y2='18'/%3E%3Cline x1='3' y1='6' x2='3.01' y2='6'/%3E%3Cline x1='3' y1='12' x2='3.01' y2='12'/%3E%3Cline x1='3' y1='18' x2='3.01' y2='18'/%3E%3C/svg%3E");
    }

    .query-icon {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%231890ff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E");
    }

    .export-icon {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%231890ff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'/%3E%3Cpolyline points='7 10 12 15 17 10'/%3E%3Cline x1='12' y1='15' x2='12' y2='3'/%3E%3C/svg%3E");
    }

    .card-title {
      font-size: 18px;
      font-weight: 600;
      color: #333;
      margin-bottom: 8px;
    }

    .card-desc {
      font-size: 14px;
      color: #666;
      line-height: 1.5;
      margin-bottom: 16px;
      height: 48px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .card-count {
      font-size: 12px;
      color: #1890ff;
      background-color: #e6f7ff;
      padding: 4px 8px;
      border-radius: 4px;
      display: inline-block;
    }
  }
}

// 3. 数据概览区样式
.home-overview {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;

  .overview-card {
    flex: 1;
    border-radius: 8px !important;

    .overview-title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid #e8e8e8;
    }

    .overview-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }

    .overview-item {
      display: flex;
      flex-direction: column;
      padding: 12px;
      background-color: #f9f9f9;
      border-radius: 4px;

      .item-label {
        font-size: 14px;
        color: #666;
        margin-bottom: 8px;
      }

      .item-value {
        font-size: 20px;
        font-weight: 600;
        color: #333;
      }

      .add-value {
        color: #52c41a;
      }

      .pending-value {
        color: #faad14;
      }
    }

    .log-table {
      .status-success {
        color: #52c41a;
      }

      .status-error {
        color: #ff4d4f;
      }
    }
  }
}

// 4. 底部样式
.home-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
  font-size: 12px;
  color: #999;

  .footer-right {
    display: flex;
    gap: 16px;

    .ant-btn {
      color: #999 !important;
      &:hover {
        color: #1890ff !important;
        background-color: transparent !important;
      }
    }
  }
}

// 响应式适配：小屏幕下改为1列布局
@media (max-width: 1200px) {
  .home-function {
    flex-direction: column;
  }

  .home-overview {
    flex-direction: column;
  }
}
</style>